import type { RoundFeed, Token } from './model';

/**
 * English title prefix of the 5-minute round events the engine created before 10/3
 * (e.g. "Bitcoin Up or Down in 5 minutes · October 3, 20:00–20:05 SGT"; U+00B7 between two spaces).
 * Kept so those rounds stay off the home list.
 */
export const SHORT_ROUND_TITLE_PREFIX = 'Bitcoin Up or Down in 5 minutes · ';

/**
 * English title of the 5-minute round events from 10/3 (dpin_5m.py TITLE_TPL["en"] =
 * "{tk} Up/Down {mon} {d} {start}–{end}", e.g. "BTC Up/Down Oct 3 20:00–20:05"; U+2013 between
 * the times). BTC only: the home card stands for the BTC series.
 */
export const SHORT_ROUND_TITLE_RE = /^BTC Up\/Down [A-Z][a-z]{2} \d{1,2} \d{2}:\d{2}–\d{2}:\d{2}$/;

export type RoundRef = { roundId: string; token: Token };

/**
 * Event id in the form the app's lists, URLs and get-event-detail use: no 0x prefix, lower case.
 * rounds.json carries 0x + 64 hex; compare ids only after this.
 */
export const normaliseEventId = (id?: string | null) => (id ?? '').trim().replace(/^0x/i, '').toLowerCase();

// Duplicate ids already reported: indexRounds runs on every feed poll, the warning once per id.
const reportedDuplicates = new Set<string>();

/**
 * Normalised event id -> its round and token, for every round market the feed lists. Rounds or
 * markets without an event id are skipped; an id listed twice keeps its first round (and warns once).
 */
export const indexRounds = (feed?: Pick<RoundFeed, 'rounds'>) => {
  const index = new Map<string, RoundRef>();
  for (const round of feed?.rounds ?? [])
    for (const token of ['DPN', 'DPS'] as const) {
      const eventId = normaliseEventId(round.markets?.[token]?.eventId);
      if (!eventId) continue;
      const first = index.get(eventId);
      if (!first) index.set(eventId, { roundId: round.id, token });
      else if (!reportedDuplicates.has(eventId)) {
        reportedDuplicates.add(eventId);
        console.warn('short-markets: event id listed twice in rounds.json', eventId, first, round.id);
      }
    }
  return index;
};

/** True for the English (base) title of an engine round event, old or short. Localized titles never match. */
export const isShortRoundTitle = (title?: string) => {
  const trimmed = title?.trim();
  return !!trimmed && (trimmed.startsWith(SHORT_ROUND_TITLE_PREFIX) || SHORT_ROUND_TITLE_RE.test(trimmed));
};

type ListedMarket = { eventId?: string; eventTitle?: string; marketName?: string };

/**
 * A home list item that belongs to a 5-minute round: its event is in the feed, or it carries the
 * engine title (rounds that already left rounds.json). The home card stands for all of them.
 */
export const isShortRoundMarket = (market: ListedMarket, index: Map<string, RoundRef>) =>
  (!!market.eventId && index.has(normaliseEventId(market.eventId))) ||
  isShortRoundTitle(market.eventTitle) ||
  isShortRoundTitle(market.marketName);
